import { sourcesApiSlice } from "api/sourcesApi.slice";
import { useFormatDate } from "common/utilities";
import { useTranslate } from "common/utilities/useTranslate";
import * as Styled from "domains/moderate/sources/components/SourceDetailSection/SourceDetailSection.styled";
import { TagFormAttribute } from "domains/moderate/sources/components/SourceDetailSection/TagFormAttribute";

type SourceDetailSectionProps = {
  sourceId: string;
};

export const SourceDetailSection = ({ sourceId }: SourceDetailSectionProps) => {
  const { getDate } = useFormatDate();
  const translate = useTranslate();

  const { data: source } = sourcesApiSlice.endpoints.getSourceById.useQuery({ sourceId });

  if (!source) return null;

  return (
    <Styled.SourceDetailContainer>
      <Styled.SourceDetailColumnContainer>
        <Styled.SourceAttributeLabel>{translate("app.moderate.sources.name.label")}</Styled.SourceAttributeLabel>
        <Styled.SourceAttributeValue>{source.name}</Styled.SourceAttributeValue>
        <Styled.SourceAttributeLabel>{translate("app.moderate.sources.description.label")}</Styled.SourceAttributeLabel>
        <Styled.SourceAttributeValue>{source.description ?? "No Description"}</Styled.SourceAttributeValue>
        <Styled.SourceAttributeLabel>{translate("app.moderate.sources.url.label")}</Styled.SourceAttributeLabel>
        <Styled.SourceAttributeValue>{source.url ?? "No URL"}</Styled.SourceAttributeValue>
        <Styled.SourceAttributeLabel>{translate("app.moderate.sources.status.label")}</Styled.SourceAttributeLabel>
        <Styled.SourceAttributeValue>{source.active ? "Active" : "Inactive"}</Styled.SourceAttributeValue>
        <Styled.SourceAttributeLabel>Last Import Time</Styled.SourceAttributeLabel>
        <Styled.SourceAttributeValue>
          {source.last_started_update ? getDate({ date: source.last_started_update }) : "None"}
        </Styled.SourceAttributeValue>
        <Styled.SourceAttributeLabel>{translate("app.moderate.sources.logo.label")}</Styled.SourceAttributeLabel>
        <Styled.SourceImage src={source.favicon} alt={`${source.name} image`} />
        {source.license && (
          <>
            <Styled.SourceAttributeLabel>{translate("app.moderate.sources.license.label")}</Styled.SourceAttributeLabel>
            <Styled.SourceAttributeValue>{source.license}</Styled.SourceAttributeValue>
          </>
        )}
        <Styled.SourceAttributeLabel>{translate("app.moderate.sources.autopilot.label")}</Styled.SourceAttributeLabel>
        <Styled.SourceAttributeValue>
          {/* Note: very messy type of boolean or string, can we improve this on the BE? - already flagged to Jono at the time of writing */}
          {source.auto_approved ? (typeof source.auto_approved === "boolean" ? "On" : source.auto_approved) : "Off"}
        </Styled.SourceAttributeValue>
      </Styled.SourceDetailColumnContainer>
      <Styled.SourceDetailColumnContainer>
        <TagFormAttribute
          label={translate("app.moderate.sources.type.label")}
          noValuePlaceholder={translate("app.moderate.sources.type.placeholder")}
          value={source.type}
        />
        <TagFormAttribute
          label={translate("app.moderate.sources.contentType.label")}
          noValuePlaceholder={translate("app.moderate.sources.contentType.placeholder")}
          value={source.content_type}
        />
        <TagFormAttribute
          label={translate("app.moderate.sources.publicationType.label")}
          noValuePlaceholder={translate("app.moderate.sources.publicationType.placeholder")}
          value={source.publication_type}
        />
        <TagFormAttribute
          label={translate("app.moderate.sources.country.label")}
          noValuePlaceholder={translate("app.moderate.sources.country.placeholder")}
          value={source.country}
        />
        <TagFormAttribute
          label={translate("app.moderate.sources.region.label")}
          noValuePlaceholder={translate("app.moderate.sources.region.placeholder")}
          value={source.region}
        />
        <TagFormAttribute
          label={translate("app.moderate.sources.greaterRegion.label")}
          noValuePlaceholder={translate("app.moderate.sources.greaterRegion.placeholder")}
          value={source.greater_region}
        />
      </Styled.SourceDetailColumnContainer>
    </Styled.SourceDetailContainer>
  );
};
